// Enhanced Cart utility functions for advanced B2B features

export const getCartKey = (userId) => `cart_${userId}`;

export const getCustomerCart = (userId) => {
  if (!userId) return [];
  const cartKey = getCartKey(userId);
  return JSON.parse(localStorage.getItem(cartKey) || '[]');
};

export const saveCustomerCart = (userId, cart) => {
  if (!userId) return;
  const cartKey = getCartKey(userId);
  localStorage.setItem(cartKey, JSON.stringify(cart));
};

export const clearCustomerCart = (userId) => {
  if (!userId) return;
  const cartKey = getCartKey(userId);
  localStorage.removeItem(cartKey);
};

export const clearOldGenericCart = () => {
  localStorage.removeItem('cart');
};

// Enhanced add to cart with bulk pricing support
export const addToCustomerCart = (userId, product, quantity = 1, options = {}) => {
  if (!userId) return [];
  
  const cart = getCustomerCart(userId);
  const existingItem = cart.find(item => item.id === product.id);
  
  if (existingItem) {
    existingItem.quantity += quantity;
    // Update any additional options
    if (options.deliveryDate) existingItem.deliveryDate = options.deliveryDate;
    if (options.specialInstructions) existingItem.specialInstructions = options.specialInstructions;
    if (options.specifications) existingItem.specifications = options.specifications;
    if (options.isUrgent !== undefined) existingItem.isUrgent = options.isUrgent;
  } else {
    cart.push({ 
      ...product, 
      quantity,
      deliveryDate: options.deliveryDate || null,
      specialInstructions: options.specialInstructions || null,
      specifications: options.specifications || null,
      isUrgent: options.isUrgent || false
    });
  }
  
  saveCustomerCart(userId, cart);
  return cart;
};

export const removeFromCustomerCart = (userId, productId) => {
  if (!userId) return [];
  
  const cart = getCustomerCart(userId);
  const updatedCart = cart.filter(item => item.id !== productId);
  saveCustomerCart(userId, updatedCart);
  return updatedCart;
};

export const updateCustomerCartQuantity = (userId, productId, newQuantity, options = {}) => {
  if (!userId) return [];
  
  const cart = getCustomerCart(userId);
  if (newQuantity <= 0) {
    return removeFromCustomerCart(userId, productId);
  }
  
  const updatedCart = cart.map(item => {
    if (item.id === productId) {
      return {
        ...item,
        quantity: newQuantity,
        deliveryDate: options.deliveryDate !== undefined ? options.deliveryDate : item.deliveryDate,
        specialInstructions: options.specialInstructions !== undefined ? options.specialInstructions : item.specialInstructions,
        specifications: options.specifications !== undefined ? options.specifications : item.specifications,
        isUrgent: options.isUrgent !== undefined ? options.isUrgent : item.isUrgent
      };
    }
    return item;
  });
  
  saveCustomerCart(userId, updatedCart);
  return updatedCart;
};

// Enhanced cart calculations with bulk pricing and customer discounts
export const calculateCartTotals = (cart, customer = null) => {
  let subtotal = 0;
  let totalDiscount = 0;
  let totalTax = 0;
  let totalShipping = 0;
  let itemCount = 0;

  cart.forEach(item => {
    // Calculate item price with bulk pricing
    let unitPrice = item.sellingPrice;
    let itemDiscount = 0;

    // Apply bulk pricing if available
    if (item.bulkPricing && item.bulkPricing.length > 0) {
      const bulkTier = item.bulkPricing.find(bp => 
        item.quantity >= bp.minQuantity && (!bp.maxQuantity || item.quantity <= bp.maxQuantity)
      );
      if (bulkTier) {
        unitPrice = bulkTier.price;
        itemDiscount = bulkTier.discount || 0;
      }
    }

    // Apply customer discount if available
    if (customer && customer.discountPercentage) {
      itemDiscount += customer.discountPercentage;
    }

    const itemSubtotal = unitPrice * item.quantity;
    const itemDiscountAmount = (itemSubtotal * itemDiscount) / 100;
    const itemSubtotalAfterDiscount = itemSubtotal - itemDiscountAmount;
    const itemTax = (itemSubtotalAfterDiscount * (item.gstPercent || 18)) / 100;

    subtotal += itemSubtotalAfterDiscount;
    totalDiscount += itemDiscountAmount;
    totalTax += itemTax;
    itemCount += item.quantity;
  });

  const total = subtotal + totalTax + totalShipping;

  return {
    subtotal,
    totalDiscount,
    totalTax,
    totalShipping,
    total,
    itemCount,
    currency: 'INR'
  };
};

export const getCustomerCartTotal = (userId, customer = null) => {
  const cart = getCustomerCart(userId);
  const totals = calculateCartTotals(cart, customer);
  return totals.total;
};

export const getCustomerCartItemCount = (userId) => {
  const cart = getCustomerCart(userId);
  return cart.reduce((count, item) => count + item.quantity, 0);
};

// Validate cart against customer constraints
export const validateCart = (cart, customer = null) => {
  const errors = [];
  const warnings = [];

  if (!customer) {
    return { errors, warnings, isValid: true };
  }

  const totals = calculateCartTotals(cart, customer);

  // Check minimum order value
  if (customer.minimumOrderValue && totals.subtotal < customer.minimumOrderValue) {
    errors.push(`Minimum order value is ₹${customer.minimumOrderValue.toLocaleString()}`);
  }

  // Check credit limit
  if (customer.creditLimit && totals.total > customer.creditLimit) {
    errors.push(`Order exceeds credit limit of ₹${customer.creditLimit.toLocaleString()}`);
  }

  // Check minimum order quantities
  cart.forEach(item => {
    if (item.minimumOrderQuantity && item.quantity < item.minimumOrderQuantity) {
      errors.push(`Minimum order quantity for ${item.name} is ${item.minimumOrderQuantity}`);
    }
    if (item.maximumOrderQuantity && item.quantity > item.maximumOrderQuantity) {
      errors.push(`Maximum order quantity for ${item.name} is ${item.maximumOrderQuantity}`);
    }
    // Note: Stock quantity validation removed for customer-facing validation
    // Customers can place orders regardless of current stock status
    // Backend may still enforce stock limits if needed
  });

  // Check for urgent items
  const urgentItems = cart.filter(item => item.isUrgent);
  if (urgentItems.length > 0) {
    warnings.push(`${urgentItems.length} item(s) marked as urgent`);
  }

  return {
    errors,
    warnings,
    isValid: errors.length === 0,
    totals
  };
};

// Prepare cart for order submission
export const prepareCartForOrder = (cart, customer = null, orderOptions = {}) => {
  const validation = validateCart(cart, customer);
  
  if (!validation.isValid) {
    throw new Error(validation.errors.join(', '));
  }

  const items = cart.map(item => ({
    productSkuId: item.productSkuId,
    quantity: item.quantity,
    deliveryDate: item.deliveryDate,
    specialInstructions: item.specialInstructions,
    specifications: item.specifications,
    isUrgent: item.isUrgent
  }));

  return {
    customerId: customer?.id,
    items,
    purchaseOrderNumber: orderOptions.purchaseOrderNumber,
    expectedDeliveryDate: orderOptions.expectedDeliveryDate,
    deliveryInstructions: orderOptions.deliveryInstructions,
    specialRequirements: orderOptions.specialRequirements,
    urgency: orderOptions.urgency || 'normal',
    paymentTerms: orderOptions.paymentTerms,
    customerNotes: orderOptions.customerNotes,
    orderType: orderOptions.orderType || 'regular',
    isUrgent: orderOptions.isUrgent || false,
    priority: orderOptions.priority || 'medium',
    validation
  };
};

// Get cart summary for display
export const getCartSummary = (userId, customer = null) => {
  const cart = getCustomerCart(userId);
  const totals = calculateCartTotals(cart, customer);
  const validation = validateCart(cart, customer);

  return {
    cart,
    totals,
    validation,
    itemCount: cart.length,
    totalItems: totals.itemCount,
    hasUrgentItems: cart.some(item => item.isUrgent),
    hasSpecialInstructions: cart.some(item => item.specialInstructions),
    estimatedDelivery: cart.length > 0 ? '3-5 business days' : null
  };
};

// Debug function to list all carts in localStorage
export const debugAllCarts = () => {
  const allCarts = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('cart_')) {
      try {
        allCarts[key] = JSON.parse(localStorage.getItem(key));
      } catch (e) {
        allCarts[key] = 'Invalid JSON';
      }
    }
  }
  console.log('All customer carts:', allCarts);
  return allCarts;
};

// Export cart data for backup/transfer
export const exportCart = (userId) => {
  const cart = getCustomerCart(userId);
  const exportData = {
    userId,
    cart,
    exportedAt: new Date().toISOString(),
    version: '1.0'
  };
  
  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `cart_${userId}_${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  
  URL.revokeObjectURL(url);
};

// Import cart data from backup
export const importCart = (userId, cartData) => {
  try {
    if (typeof cartData === 'string') {
      cartData = JSON.parse(cartData);
    }
    
    if (cartData.cart && Array.isArray(cartData.cart)) {
      saveCustomerCart(userId, cartData.cart);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to import cart:', error);
    return false;
  }
}; 